const DB_NAME = 'powermate-diagram-thumbnails';
const DB_VERSION = 2;
const STORE_NAME = 'diagramThumbnails';

const THUMBNAIL_VIEWBOX_WIDTH = 220;
const THUMBNAIL_VIEWBOX_HEIGHT = 86;
const THUMBNAIL_PADDING = 6;
const FALLBACK_NODE_WIDTH = 85;
const FALLBACK_NODE_HEIGHT = 85;
const MIN_BLOCK_SIZE = 10;
const MAX_BLOCK_WIDTH = 30;
const MAX_BLOCK_HEIGHT = 22;
const SMALL_DIAGRAM_MAX_BLOCK_SIZE = 16;
const THUMBNAIL_PALETTES = {
    diagram: {
        connection: '#7fb7d4',
        nodeStroke: '#2586bc',
        nodeMark: '#61afd2',
    },
    automate: {
        connection: '#52b788',
        nodeStroke: '#52b788',
        nodeMark: '#74c69d',
    },
};
const COMPACT_TWO_NODE_SIZE = 16;
const COMPACT_TWO_NODE_GAP = 26;
const COMPACT_FOUR_NODE_SIZE = 15;
const COMPACT_FOUR_NODE_GAP_X = 22;
const COMPACT_FOUR_NODE_GAP_Y = 14;
const COMPACT_SIX_NODE_SIZE = 14;
const COMPACT_SIX_NODE_GAP_X = 18;
const COMPACT_SIX_NODE_GAP_Y = 12;
let dbPromise = null;

const logThumbnailEvent = (icon, label, payload = {}) => {
    if (typeof window === 'undefined') {
        return;
    }

    console.log(`${icon} [Thumbnail] ${label}`, payload);
};

const isIndexedDbAvailable = () =>
    typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';

const resetDiagramThumbnailDb = (database) => {
    if (database && typeof database.close === 'function') {
        try {
            database.close();
        } catch (error) {
            // noop
        }
    }

    dbPromise = null;
};

const openDiagramThumbnailDb = () => {
    if (!isIndexedDbAvailable()) {
        return Promise.resolve(null);
    }

    if (dbPromise) {
        return dbPromise;
    }

    dbPromise = new Promise((resolve, reject) => {
        const request = window.indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
            const database = request.result;

            if (database.objectStoreNames.contains(STORE_NAME)) {
                database.deleteObjectStore(STORE_NAME);
            }

            database.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
        };

        request.onsuccess = () => {
            const database = request.result;

            database.onversionchange = () => {
                resetDiagramThumbnailDb(database);
            };

            database.onclose = () => {
                resetDiagramThumbnailDb();
            };

            resolve(database);
        };
        request.onerror = () => reject(request.error);
    }).catch((error) => {
        dbPromise = null;
        throw error;
    });

    return dbPromise;
};

const runStoreRequest = async (mode, callback) => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
        const database = await openDiagramThumbnailDb();

        if (!database) {
            return null;
        }

        try {
            if (!database.objectStoreNames.contains(STORE_NAME)) {
                resetDiagramThumbnailDb(database);
                continue;
            }

            return await new Promise((resolve, reject) => {
                const transaction = database.transaction(STORE_NAME, mode);
                const store = transaction.objectStore(STORE_NAME);
                const request = callback(store);

                transaction.oncomplete = () => {
                    resolve(request?.result ?? null);
                };
                transaction.onerror = () => {
                    reject(transaction.error || request?.error);
                };
                transaction.onabort = () => {
                    reject(transaction.error || request?.error);
                };
            });
        } catch (error) {
            const shouldRetry = (
                error?.name === 'InvalidStateError' ||
                error?.name === 'NotFoundError'
            );

            resetDiagramThumbnailDb(database);

            if (shouldRetry && attempt === 0) {
                continue;
            }

            throw error;
        }
    }

    return null;
};

const normalizeDiagramId = (diagramId) => {
    const numericId = Number(diagramId);
    return Number.isFinite(numericId) ? numericId : `${diagramId}`;
};

const normalizeOwnerType = (ownerType = 'diagram') => `${ownerType || 'diagram'}`;
const buildThumbnailCacheKey = (ownerType = 'diagram', ownerId) => {
    if (!ownerId) {
        return null;
    }

    return `${normalizeOwnerType(ownerType)}:${normalizeDiagramId(ownerId)}`;
};

const getNodeSnapshot = (node, index) => {
    const x = Number(node?.position?.x);
    const y = Number(node?.position?.y);
    const width = Number(node?.width) || FALLBACK_NODE_WIDTH;
    const height = Number(node?.height) || FALLBACK_NODE_HEIGHT;

    if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
    }

    return {
        id: node?.id || node?._id || `node-${index}`,
        x,
        y,
        width,
        height,
    };
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const buildThumbnailBlocks = (nodes = []) => {
    const positionedNodes = nodes
        .map((node, index) => getNodeSnapshot(node, index))
        .filter(Boolean);

    if (positionedNodes.length === 0) {
        return [];
    }

    if (positionedNodes.length === 1) {
        return [{
            id: positionedNodes[0].id,
            x: Math.round((THUMBNAIL_VIEWBOX_WIDTH - COMPACT_TWO_NODE_SIZE) / 2),
            y: Math.round((THUMBNAIL_VIEWBOX_HEIGHT - COMPACT_TWO_NODE_SIZE) / 2),
            width: COMPACT_TWO_NODE_SIZE,
            height: COMPACT_TWO_NODE_SIZE,
        }];
    }

    if (positionedNodes.length === 2) {
        const orderedNodes = [...positionedNodes].sort((leftNode, rightNode) => {
            if (leftNode.x === rightNode.x) {
                return leftNode.y - rightNode.y;
            }

            return leftNode.x - rightNode.x;
        });
        const totalWidth = (COMPACT_TWO_NODE_SIZE * 2) + COMPACT_TWO_NODE_GAP;
        const startX = Math.round((THUMBNAIL_VIEWBOX_WIDTH - totalWidth) / 2);
        const y = Math.round((THUMBNAIL_VIEWBOX_HEIGHT - COMPACT_TWO_NODE_SIZE) / 2);

        return orderedNodes.map((node, index) => ({
            id: node.id,
            x: startX + (index * (COMPACT_TWO_NODE_SIZE + COMPACT_TWO_NODE_GAP)),
            y,
            width: COMPACT_TWO_NODE_SIZE,
            height: COMPACT_TWO_NODE_SIZE,
        }));
    }

    if (positionedNodes.length === 4) {
        const orderedNodes = [...positionedNodes].sort((leftNode, rightNode) => {
            if (leftNode.y === rightNode.y) {
                return leftNode.x - rightNode.x;
            }

            return leftNode.y - rightNode.y;
        });
        const totalWidth = (COMPACT_FOUR_NODE_SIZE * 2) + COMPACT_FOUR_NODE_GAP_X;
        const totalHeight = (COMPACT_FOUR_NODE_SIZE * 2) + COMPACT_FOUR_NODE_GAP_Y;
        const startX = Math.round((THUMBNAIL_VIEWBOX_WIDTH - totalWidth) / 2);
        const startY = Math.round((THUMBNAIL_VIEWBOX_HEIGHT - totalHeight) / 2);

        return orderedNodes.map((node, index) => {
            const row = Math.floor(index / 2);
            const column = index % 2;

            return {
                id: node.id,
                x: startX + (column * (COMPACT_FOUR_NODE_SIZE + COMPACT_FOUR_NODE_GAP_X)),
                y: startY + (row * (COMPACT_FOUR_NODE_SIZE + COMPACT_FOUR_NODE_GAP_Y)),
                width: COMPACT_FOUR_NODE_SIZE,
                height: COMPACT_FOUR_NODE_SIZE,
            };
        });
    }

    if (positionedNodes.length === 6) {
        const orderedNodes = [...positionedNodes].sort((leftNode, rightNode) => {
            if (leftNode.y === rightNode.y) {
                return leftNode.x - rightNode.x;
            }

            return leftNode.y - rightNode.y;
        });
        const totalWidth = (COMPACT_SIX_NODE_SIZE * 3) + (COMPACT_SIX_NODE_GAP_X * 2);
        const totalHeight = (COMPACT_SIX_NODE_SIZE * 2) + COMPACT_SIX_NODE_GAP_Y;
        const startX = Math.round((THUMBNAIL_VIEWBOX_WIDTH - totalWidth) / 2);
        const startY = Math.round((THUMBNAIL_VIEWBOX_HEIGHT - totalHeight) / 2);

        return orderedNodes.map((node, index) => {
            const row = Math.floor(index / 3);
            const column = index % 3;

            return {
                id: node.id,
                x: startX + (column * (COMPACT_SIX_NODE_SIZE + COMPACT_SIX_NODE_GAP_X)),
                y: startY + (row * (COMPACT_SIX_NODE_SIZE + COMPACT_SIX_NODE_GAP_Y)),
                width: COMPACT_SIX_NODE_SIZE,
                height: COMPACT_SIX_NODE_SIZE,
            };
        });
    }

    const bounds = positionedNodes.reduce((accumulator, node) => ({
        minX: Math.min(accumulator.minX, node.x),
        maxX: Math.max(accumulator.maxX, node.x + node.width),
        minY: Math.min(accumulator.minY, node.y),
        maxY: Math.max(accumulator.maxY, node.y + node.height),
    }), {
        minX: Infinity,
        maxX: -Infinity,
        minY: Infinity,
        maxY: -Infinity,
    });

    const usableWidth = THUMBNAIL_VIEWBOX_WIDTH - (THUMBNAIL_PADDING * 2);
    const usableHeight = THUMBNAIL_VIEWBOX_HEIGHT - (THUMBNAIL_PADDING * 2);
    const rangeX = Math.max(bounds.maxX - bounds.minX, 1);
    const rangeY = Math.max(bounds.maxY - bounds.minY, 1);
    const scale = Math.min(usableWidth / rangeX, usableHeight / rangeY);
    const scaledWidth = rangeX * scale;
    const scaledHeight = rangeY * scale;
    const offsetX = THUMBNAIL_PADDING + ((usableWidth - scaledWidth) / 2);
    const offsetY = THUMBNAIL_PADDING + ((usableHeight - scaledHeight) / 2);
    const maxSquareSize = positionedNodes.length <= 6
        ? SMALL_DIAGRAM_MAX_BLOCK_SIZE
        : Math.min(MAX_BLOCK_WIDTH, MAX_BLOCK_HEIGHT);

    return positionedNodes.map((node) => {
        const squareSize = clamp(
            Math.round(Math.min(node.width, node.height) * scale),
            MIN_BLOCK_SIZE,
            maxSquareSize
        );

        return {
            id: node.id,
            x: Math.round(offsetX + ((node.x - bounds.minX) * scale)),
            y: Math.round(offsetY + ((node.y - bounds.minY) * scale)),
            width: squareSize,
            height: squareSize,
        };
    });
};

const getBlockAnchorPoint = (block, side = 'right') => {
    if (side === 'left') {
        return {
            x: block.x,
            y: block.y + (block.height / 2),
        };
    }

    return {
        x: block.x + block.width,
        y: block.y + (block.height / 2),
    };
};

const buildThumbnailConnections = (blocks = [], edges = []) => {
    const blocksMap = blocks.reduce((accumulator, block) => {
        accumulator[block.id] = block;
        return accumulator;
    }, {});

    return (edges || []).reduce((accumulator, edge, index) => {
        const sourceBlock = blocksMap[edge?.source];
        const targetBlock = blocksMap[edge?.target];

        if (!sourceBlock || !targetBlock) {
            return accumulator;
        }

        const targetIsOnRight = sourceBlock.x <= targetBlock.x;
        const sourcePoint = getBlockAnchorPoint(sourceBlock, targetIsOnRight ? 'right' : 'left');
        const targetPoint = getBlockAnchorPoint(targetBlock, targetIsOnRight ? 'left' : 'right');
        const midX = Math.round((sourcePoint.x + targetPoint.x) / 2);

        accumulator.push({
            id: `${edge?.id || `${edge?.source}-${edge?.target}`}-${index}`,
            path: `M ${sourcePoint.x} ${sourcePoint.y} L ${midX} ${sourcePoint.y} L ${midX} ${targetPoint.y} L ${targetPoint.x} ${targetPoint.y}`,
        });

        return accumulator;
    }, []);
};

const createThumbnailSvgMarkup = ({
    blocks = [],
    edges = [],
    ownerType = 'diagram',
} = {}) => {
    if (blocks.length === 0) {
        return null;
    }

    const palette = THUMBNAIL_PALETTES[normalizeOwnerType(ownerType)] || THUMBNAIL_PALETTES.diagram;

    const connections = buildThumbnailConnections(blocks, edges);
    const connectionsMarkup = connections.map((connection) => `
        <path
            d="${connection.path}"
            fill="none"
            stroke="${palette.connection}"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round"
        />
    `).join('');

    const content = blocks.map((block) => {
        const midY = block.y + (block.height / 2);
        return `
            <g>
                <rect
                    x="${block.x}"
                    y="${block.y}"
                    width="${block.width}"
                    height="${block.height}"
                    rx="1.5"
                    fill="#ffffff"
                    stroke="${palette.nodeStroke}"
                    stroke-width="1.6"
                />
                <line
                    x1="${block.x + 3}"
                    y1="${midY - 2}"
                    x2="${block.x + block.width - 3}"
                    y2="${midY - 2}"
                    stroke="${palette.nodeMark}"
                    stroke-width="1"
                    stroke-linecap="round"
                />
                <line
                    x1="${block.x + 3}"
                    y1="${midY + 2}"
                    x2="${block.x + Math.max(block.width - 6, 5)}"
                    y2="${midY + 2}"
                    stroke="${palette.nodeMark}"
                    stroke-width="1"
                    stroke-linecap="round"
                />
            </g>
        `;
    }).join('');

    return `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${THUMBNAIL_VIEWBOX_WIDTH} ${THUMBNAIL_VIEWBOX_HEIGHT}">
            ${connectionsMarkup}
            ${content}
        </svg>
    `.trim();
};

export const createDiagramThumbnailDataUrl = ({
    nodes = [],
    edges = [],
    ownerType = 'diagram',
} = {}) => {
    const blocks = buildThumbnailBlocks(nodes);
    const svgMarkup = createThumbnailSvgMarkup({ blocks, edges, ownerType });

    if (!svgMarkup) {
        return null;
    }

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgMarkup)}`;
};

export const captureDiagramThumbnailDataUrl = async () => null;

export const saveDiagramThumbnail = async ({
    diagramId,
    ownerId = diagramId,
    ownerType = 'diagram',
    name,
    nodes = [],
    edges = [],
} = {}) => {
    const cacheKey = buildThumbnailCacheKey(ownerType, ownerId);

    if (!cacheKey) {
        return null;
    }

    logThumbnailEvent('🖼️', 'Generando thumbnail SVG', {
        ownerType,
        ownerId,
        diagramId,
        name,
        nodes: nodes?.length || 0,
        edges: edges?.length || 0,
        cacheKey,
    });

    const imageDataUrl = createDiagramThumbnailDataUrl({ nodes, edges, ownerType });

    if (!imageDataUrl) {
        logThumbnailEvent('⚠️', 'No se pudo generar thumbnail SVG', {
            ownerType,
            ownerId,
            diagramId,
            name,
            cacheKey,
        });
        return null;
    }

    const record = {
        cacheKey,
        diagramId: diagramId ? normalizeDiagramId(diagramId) : null,
        ownerId: normalizeDiagramId(ownerId),
        ownerType: normalizeOwnerType(ownerType),
        name: name || '',
        imageDataUrl,
        updatedAt: new Date().toISOString(),
    };

    await runStoreRequest('readwrite', (store) => store.put(record));
    logThumbnailEvent('💾', 'Thumbnail guardado en IndexedDB', {
        ownerType,
        ownerId: record.ownerId,
        diagramId: record.diagramId,
        name: record.name,
        cacheKey: record.cacheKey,
        source: 'svg',
        updatedAt: record.updatedAt,
    });
    return record;
};

export const saveDiagramDomThumbnail = async ({
    diagramId,
    ownerId = diagramId,
    ownerType = 'diagram',
    name,
    nodes = [],
    edges = [],
    captureElement,
    reactFlowInstance,
    fitView = true,
} = {}) => {
    const imageDataUrl = await captureDiagramThumbnailDataUrl({
        captureElement,
        reactFlowInstance,
        nodes,
        fitView,
    });

    if (imageDataUrl) {
        const cacheKey = buildThumbnailCacheKey(ownerType, ownerId);
        logThumbnailEvent('📸', 'Captura real generada', {
            ownerType,
            ownerId,
            diagramId,
            name,
            nodes: nodes?.length || 0,
            edges: edges?.length || 0,
            cacheKey,
        });
        const record = {
            cacheKey,
            diagramId: diagramId ? normalizeDiagramId(diagramId) : null,
            ownerId: normalizeDiagramId(ownerId),
            ownerType: normalizeOwnerType(ownerType),
            name: name || '',
            imageDataUrl,
            updatedAt: new Date().toISOString(),
        };

        await runStoreRequest('readwrite', (store) => store.put(record));
        logThumbnailEvent('💾', 'Captura guardada en IndexedDB', {
            ownerType,
            ownerId: record.ownerId,
            diagramId: record.diagramId,
            name: record.name,
            cacheKey: record.cacheKey,
            source: 'capture',
            updatedAt: record.updatedAt,
        });
        return record;
    }

    logThumbnailEvent('⚠️', 'Fallo la captura real, uso fallback SVG', {
        ownerType,
        ownerId,
        diagramId,
        name,
        nodes: nodes?.length || 0,
        edges: edges?.length || 0,
    });

    return saveDiagramThumbnail({
        diagramId,
        ownerId,
        ownerType,
        name,
        nodes,
        edges,
    });
};

export const getDiagramThumbnail = async (diagramId) => {
    return getStoredThumbnail({ ownerId: diagramId, ownerType: 'diagram' });
};

export const getStoredThumbnail = async ({
    ownerId,
    ownerType = 'diagram',
} = {}) => {
    const cacheKey = buildThumbnailCacheKey(ownerType, ownerId);

    if (!cacheKey) {
        return null;
    }

    return runStoreRequest('readonly', (store) => store.get(cacheKey));
};

export const getDiagramThumbnailMap = async (diagramIds = []) => {
    return getStoredThumbnailMap({ ownerIds: diagramIds, ownerType: 'diagram' });
};

export const getStoredThumbnailMap = async ({
    ownerIds = [],
    ownerType = 'diagram',
} = {}) => {
    const uniqueIds = [...new Set(
        (ownerIds || [])
            .filter(Boolean)
            .map((ownerId) => normalizeDiagramId(ownerId))
    )];

    if (uniqueIds.length === 0) {
        return {};
    }

    const records = await Promise.all(
        uniqueIds.map((ownerId) => getStoredThumbnail({ ownerId, ownerType }))
    );

    return records.reduce((accumulator, record) => {
        if (record?.ownerId && record?.imageDataUrl) {
            accumulator[record.ownerId] = record.imageDataUrl;
        }
        return accumulator;
    }, {});
};

export const removeDiagramThumbnail = async (diagramId) => {
    return removeStoredThumbnail({ ownerId: diagramId, ownerType: 'diagram' });
};

export const removeStoredThumbnail = async ({
    ownerId,
    ownerType = 'diagram',
} = {}) => {
    const cacheKey = buildThumbnailCacheKey(ownerType, ownerId);

    if (!cacheKey) {
        return null;
    }

    return runStoreRequest('readwrite', (store) => store.delete(cacheKey));
};
